import './DatasetForm.css';

import { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router';
import Paginator from '../Paginator/Paginator';
import FormHeader from './components/FormHeader/FormHeader';
import FormItemResolver from './components/FormItemResolver/FormItemResolver';
import SeptimLoadingSpinner from '../SeptimLoadingSpinner/SeptimLoadingSpinner';
import { useFormOptionsPrefetch } from './hooks/useFormOptionsPrefetch';
import { useFormLoadingPosition } from './hooks/useFormLoadingPosition';
import { DatasetFormProps, DatasetFormWrapperProps } from './types';
import { useDatasetContext } from '../../../hooks/context/useDatasetContext';
import { useDatasetFormData } from '../../../hooks/context/useDatasetFormData';
import { getFormLabel } from '../../../utils/forms/getFormLabel';
import { useBreadCrumbs } from '../../../hooks/useBreadCrumbs';
import BreadCrumbs from '../BreadCrumbs/BreadCrumbs';
import useUnsavedChangesModal from '../../../hooks/useUnsavedChangesModal';
import { useSelectedRowsContext } from '../../../hooks/context/useSelectedRowsContext';
import FormInitProvider from '../../../context/FormInitContext';
import { useAutoFocusFirstFormField } from '../../../hooks/useAutoFocusFirstFormField';
import { GlobalLocationStateType } from '../../../types/types';
import { useGuideContext } from '../../../context/GuideContext/GuideContext';

const DatasetForm = ({ openFullscreenReference }: DatasetFormProps) => {
  const formContentRef = useRef<HTMLDivElement>(null);
  const {
    controlSelectionMode,
    currentSelectedRowIndex,
    form,
    formItems,
    formType,
    multiSelectMode,
    showNextRow,
    showPreviousRow,
    isSubmitting,
    formValuesChanged,
    submitDatasetForm,
  } = useDatasetFormData();
  const { registerFormNavigationGuard } = useGuideContext();
  const { referenceDatasets, setReferenceDatasets, dataset } =
    useDatasetContext();
  const { selectedRows } = useSelectedRowsContext();
  const breadcrumbs = useBreadCrumbs();
  const { pathname } = useLocation<GlobalLocationStateType>();
  const { navigate: navigateWithUnsavedCheck, modalElement } =
    useUnsavedChangesModal();
  const { spinnerPos } = useFormLoadingPosition(isSubmitting, formContentRef);
  const autoFocusFirstFormField = useAutoFocusFirstFormField(formContentRef);

  useEffect(
    () =>
      registerFormNavigationGuard({
        hasUnsavedChanges: formValuesChanged,
        save: submitDatasetForm,
      }),
    [formValuesChanged, registerFormNavigationGuard, submitDatasetForm]
  );

  // Seed first-option defaults for dynamic radioBtns whose tabs may never be
  // visited — otherwise submit would send "" instead of the first option's value.
  useFormOptionsPrefetch();

  const showPaginator: boolean = useMemo(
    // 'dontcare' means no rows were selected; paginator is irrelevant in that case
    () =>
      controlSelectionMode !== 'dontcare' &&
      multiSelectMode === 'sequential' &&
      selectedRows.length > 1,
    [controlSelectionMode, multiSelectMode, selectedRows.length]
  );

  //TODO: Implement sprintf formatting of form title
  const formTitle: string = useMemo(
    () => getFormLabel(formType, form, dataset, pathname),
    [formType, form, dataset, pathname]
  );

  return (
    <>
      <BreadCrumbs breadcrumbs={breadcrumbs} />
      <FormHeader
        header={formTitle}
        paginator={
          showPaginator && (
            <div className="right-buttons">
              <Paginator
                currentStep={currentSelectedRowIndex + 1}
                numberOfSteps={selectedRows.length}
                previous={() => navigateWithUnsavedCheck(showPreviousRow)}
                next={() => navigateWithUnsavedCheck(showNextRow)}
              />
            </div>
          )
        }
      />
      <div className="form-content" ref={formContentRef}>
        {isSubmitting && <div className="form-submit-overlay" />}
        {isSubmitting &&
          spinnerPos !== null &&
          createPortal(
            <div
              className="form-submit-spinner"
              style={{ left: spinnerPos.left, top: spinnerPos.top }}
            >
              <SeptimLoadingSpinner />
            </div>,
            document.body
          )}
        <div className="form-container">
          <FormInitProvider onInitialized={autoFocusFirstFormField}>
            {formItems.map((item, index) => (
              <FormItemResolver
                key={index}
                formItem={item}
                openFullscreenReference={openFullscreenReference}
                referenceDatasets={referenceDatasets}
                setReferenceDatasets={setReferenceDatasets}
              />
            ))}
          </FormInitProvider>
        </div>
      </div>
      {modalElement}
    </>
  );
};

const DatasetFormWrapper = ({
  openFullscreenReference,
}: DatasetFormWrapperProps) => {
  return <DatasetForm openFullscreenReference={openFullscreenReference} />;
};

export default DatasetFormWrapper;
